import { API_BASE_URL, ENDPOINTS } from "../config/endpoints";
import { tokenService } from "../utils/tokenService";

export type ApiAuthMode = "required" | "none";

export type ApiCacheMode =
  | { mode: "none" }
  | { mode: "memory"; ttlMs: number };

export interface ApiOptions extends Omit<RequestInit, "body" | "headers"> {
  body?: unknown;
  headers?: HeadersInit;
  /**
   * Backwards-compatible flag. Prefer `auth: "none"`.
   */
  skipAuth?: boolean;
  /**
   * Whether auth is required for this request. Defaults to "required".
   * If set to "none", Authorization header will not be attached and refresh won't be attempted.
   */
  auth?: ApiAuthMode;
  /**
   * Retry once after refreshing on 401. Defaults to true when auth is required.
   */
  retryOn401?: boolean;
  /**
   * Optional in-memory cache for GET requests (opt-in, per request).
   */
  apiCache?: ApiCacheMode;
}

export type ApiErrorPayload = {
  message?: string;
  code?: string;
  errors?: unknown;
  [k: string]: unknown;
};

export class ApiError extends Error {
  readonly status: number;
  readonly url: string;
  readonly payload?: ApiErrorPayload;

  constructor(args: { message: string; status: number; url: string; payload?: ApiErrorPayload }) {
    super(args.message);
    this.name = "ApiError";
    this.status = args.status;
    this.url = args.url;
    this.payload = args.payload;
  }
}

type CacheEntry = { expiresAt: number; value: unknown };
const memoryCache = new Map<string, CacheEntry>();

const makeCacheKey = (method: string, url: string, headers: HeadersInit | undefined) => {
  // include auth scope (token) to avoid cross-user cache bleed
  const authHeader = headers ? new Headers(headers).get("Authorization") ?? "" : "";
  return `${method.toUpperCase()} ${url} ${authHeader}`;
};

let refreshPromise: Promise<string> | null = null;

async function getRefreshedAccessToken(): Promise<string> {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    const refreshToken = tokenService.getRefreshToken();

    if (!refreshToken) {
      tokenService.clearAllData();
      throw new Error("Session expired");
    }

    const refreshRes = await fetch(`${API_BASE_URL}${ENDPOINTS.AUTH.REFRESH}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refreshToken }),
    });

    if (!refreshRes.ok) {
      tokenService.clearAllData();
      throw new Error("Session expired");
    }

    const refreshData = (await refreshRes.json()) as { accessToken?: string; refreshToken?: string };
    tokenService.setTokens({
      accessToken: refreshData.accessToken ?? "",
      refreshToken: refreshData.refreshToken ?? "",
    });

    if (!refreshData.accessToken) {
      tokenService.clearAllData();
      throw new Error("Session expired");
    }

    return refreshData.accessToken;
  })();

  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
}

// api/client/apiClient.ts
export const apiClient = async <T = unknown>(
  url: string,
  options: ApiOptions = {}
): Promise<T> => {
  const token = tokenService.getAccessToken();
  const fullUrl = url.startsWith("http") ? url : `${API_BASE_URL}${url}`;
  const isFormData = options.body instanceof FormData;

  const body = options.body && !isFormData ? JSON.stringify(options.body) : (options.body as any);

  const authMode: ApiAuthMode =
    options.auth ?? (options.skipAuth ? "none" : "required");
  const shouldRetryOn401 =
    options.retryOn401 ?? (authMode === "required");

  const getHeaders = (accessToken?: string): HeadersInit => ({
    ...(authMode === "required" && accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    ...(!isFormData && options.body ? { "Content-Type": "application/json" } : {}),
    ...(options.headers || {}),
  });

  // Optional per-request caching for GETs
  const method = (options.method ?? "GET").toUpperCase();
  const cacheMode = options.apiCache ?? { mode: "none" as const };
  const headersForKey = getHeaders(token ?? undefined);
  const cacheKey =
    method === "GET" && cacheMode.mode === "memory"
      ? makeCacheKey(method, fullUrl, headersForKey)
      : null;

  if (cacheKey) {
    const entry = memoryCache.get(cacheKey);
    if (entry && entry.expiresAt > Date.now()) {
      return entry.value as T;
    }
  }

  let response = await fetch(fullUrl, {
    ...options,
    headers: headersForKey,
    body,
  });

  // Only attempt refresh if it's a 401 AND NOT an explicit auth/skipAuth request
  if (response.status === 401 && authMode === "required" && shouldRetryOn401) {
    try {
      const newAccessToken = await getRefreshedAccessToken();
      response = await fetch(fullUrl, {
        ...options,
        headers: getHeaders(newAccessToken),
        body,
      });
    } catch {
      tokenService.clearAllData();
      throw new Error("Session expired");
    }
  }

  // Handle all errors (including 401s for OTP)
  if (!response.ok) {
    const contentType = response.headers.get("content-type") || "";
    const isJson = contentType.includes("application/json");
    const errorData: ApiErrorPayload = isJson
      ? await response.json().catch(() => ({}))
      : {};
    throw new ApiError({
      status: response.status,
      url: fullUrl,
      message: errorData.message || "Something went wrong",
      payload: errorData,
    });
  }

  if (response.status === 204) {
    const result = undefined as unknown as T;
    if (cacheKey) {
      memoryCache.set(cacheKey, { value: result, expiresAt: Date.now() + (cacheMode as any).ttlMs });
    }
    return result;
  }

  const contentType = response.headers.get("content-type") || "";
  const isJson = contentType.includes("application/json");
  const result = (isJson ? await response.json() : await response.text()) as T;

  if (cacheKey) {
    memoryCache.set(cacheKey, {
      value: result,
      expiresAt: Date.now() + (cacheMode as { mode: "memory"; ttlMs: number }).ttlMs,
    });
  }

  return result;
};